// @flow

import { useState } from 'react';

import { Button, View } from 'reshaped';

import { useTranslator } from '@gandi/translate';

import { Address } from 'src/Cart/AccountChoice/componentsNDS/ContactBlock';
import type { AddressProps } from 'src/Cart/AccountChoice/componentsNDS/ContactBlock';
import { EditButton } from 'src/Cart/AccountChoice/componentsNDS/EditButton';

import { Contact } from '../Contact';
import { SubmitButton } from '../Modal/Content/SubmitButton';
import type { Item } from '../RadioBlock';
import { RadioBlockGroup } from '../RadioBlockGroup';
import type { OpenModalFunction } from '../hooks/useModalType';

type Props<T: Item> = {
  initialValue?: string,
  items: Array<T>,
  listFooterNotice?: string,
  notFoundMessage?: string,
  checkMissingVAT?: boolean,
  checkMissingInfo?: boolean,
  openModal: OpenModalFunction,
  handleEdit: (value: string) => Promise<void> | void,
  handleSubmit: (value: string) => Promise<void>,
  permissionsToSubmit?: Array<string>,
  type: 'organization' | 'customer',
};

export function SelectContactForm<T: Item>({
  initialValue,
  items,
  listFooterNotice,
  notFoundMessage,
  checkMissingVAT = false,
  checkMissingInfo = false,
  handleEdit,
  handleSubmit,
  openModal,
  permissionsToSubmit,
  type,
}: Props<T>): React.MixedElement {
  const [value, setValue] = useState(initialValue);
  const [editing, setEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { __ } = useTranslator();

  return (
    <View as="form" justify="space-between" height="100%">
      {/* list */}
      {/* $FlowFixMe[incompatible-type] incompatible typing between renderLabel prop and Contact */}
      <RadioBlockGroup
        name="contact"
        disabled={editing || submitting}
        initialValue={initialValue}
        value={value}
        setValue={setValue}
        items={items}
        renderLabel={Contact}
        listFooterNotice={listFooterNotice}
        renderNoOpt={notFoundMessage}
      >
        {(item: AddressProps['orga']) => (
          <Address
            orga={item}
            type={type}
            checkMissing={checkMissingInfo}
            checkMissingVAT={checkMissingVAT}
          />
        )}
      </RadioBlockGroup>

      {/* Footer Actions */}
      {value && (
        <View
          as="div"
          gap={3}
          direction="row"
          position="sticky"
          backgroundColor="elevation-raised"
          paddingBlock={4}
          paddingInline={8}
          insetBottom={-7}
          bleed={8}
          attributes={{
            style: { marginBottom: 'calc(-1 * var(--rs-unit-x8))' },
          }}
        >
          {type === 'customer' ? (
            <Button
              key="edit-button"
              onClick={async () => {
                try {
                  setEditing(true);
                  await handleEdit(value);
                } catch {
                  // bypass
                } finally {
                  setEditing(false);
                }
              }}
              size="large"
              variant="outline"
              attributes={{ style: { flex: 1 } }}
              disabled={submitting}
              loading={editing}
            >
              {__('Edit')}
            </Button>
          ) : (
            <EditButton
              handleEdit={handleEdit}
              value={value}
              disabled={submitting}
            />
          )}

          <SubmitButton
            key="submit-button"
            value={value}
            loading={submitting}
            disabled={editing}
            onClick={async () => {
              try {
                setSubmitting(true);
                await handleSubmit(value);
              } catch {
                // bypass
              } finally {
                setSubmitting(false);
              }
            }}
            openModal={openModal}
            permissions={permissionsToSubmit}
          />
        </View>
      )}
    </View>
  );
}
